import { getState } from '../routing/history';
import { getPagePopupContainer } from '../popups/page-popup';
import Logger from '../logging/logger';
import requestIdleCallback from '../shims/requestIdleCallback';
import { captureException } from '../logging/sentry';

function onPopupOpen( event: CustomEvent ): void {
    let detail = event.detail.srcEvent || {};

    if ( event.detail.useHistory && event.detail.useHistory === true ) {
        detail = history.state;
    }

    if ( !detail.url ) {
        throw new Error( `popup url is undefined, ${JSON.stringify( detail )}` );
    }

    Logger.info( 'Event Triggered: onPopupOpen %O', detail );

    let srcURL: URL | null = null;

    try {
        srcURL = new URL( detail.url );
    } catch ( err ) {
        srcURL = null;
        throw err;
    }

    let elementToScrollTo: HTMLElement | null = null;

    const shouldScroll = srcURL && srcURL.searchParams.has( 'scrollTo' ) && srcURL.searchParams.get( 'scrollTo' );

    if ( shouldScroll ) {
        elementToScrollTo = document.getElementById( srcURL.searchParams.get( 'scrollTo' ) as string );
    }

    if ( shouldScroll && elementToScrollTo !== null && !detail.isRoutedPopup ) {
        elementToScrollTo.scrollIntoView( true );
        Logger.info( 'Action Triggered: scroll to target, popup=no %O', detail );
    }

    const popupContainer = getPagePopupContainer();

    const elementToScroll = popupContainer.querySelector( '.pagepopup__scrollcontainer' );

    if ( !elementToScroll ) {
        captureException( new Error( 'find to find popup scroll container' ) );
        return;
    }

    let positionToScrollTo = 0;

    if ( shouldScroll && elementToScrollTo !== null && detail.isRoutedPopup ) {
        const rect: DOMRect = elementToScrollTo.getBoundingClientRect();
        const header = popupContainer.querySelector( '.pagepopup__header' ) as HTMLElement;

        positionToScrollTo = ( rect.y - header.offsetHeight ) - 20;

        Logger.info( `Action Triggered: scroll to target (${positionToScrollTo})`, {
            isRoutedPopup: detail.isRoutedPopup,
            headerOffset: header.offsetHeight,
            rect: {
                y: rect.y,
            },
        } );
    }

    if ( !shouldScroll && !elementToScrollTo ) {
        positionToScrollTo = detail.scrollPosition;
        Logger.info( `Action Triggered: scroll to (${detail.scrollPosition}) default`, detail );
    }

    window.requestAnimationFrame( () => {
        elementToScroll.scrollTop = positionToScrollTo;

        requestIdleCallback( async () => {
            captureScrollPosition();
        } );
    } );
}

async function captureScrollPosition(): Promise<void> {
    const newState = Object.assign( {}, getState(), {
        scrollPosition: window.router.GetScrollOffset().value,
    } );

    if ( !newState.url ) {
        return;
    }

    window.router.Replace( 'current', newState );
}

export function getOnPopupScroll(): EventListener {
    let scrollingTimeout: number | null = null;
    return function( event: Event ): void {
        if ( scrollingTimeout !== null ) {
            window.clearTimeout( scrollingTimeout );
        }

        scrollingTimeout = window.setTimeout( async () => {
            scrollingTimeout = null;
            await captureScrollPosition();
        }, 250 );
    };
}

export default onPopupOpen;
